import { useEffect, useState } from 'react';
import {
  Navigate,
  Outlet,
  redirect,
  useLoaderData,
  useLocation,
  useNavigate,
  useNavigation,
  useParams,
  useSearchParams,
  type LoaderFunctionArgs,
  type RouteObject,
  type ShouldRevalidateFunctionArgs,
} from 'react-router-dom';

import { parseProjectAddress, TAB_SLUGS } from '@scoutaround/script-breakdown-shared';

import { api, clearToken, getToken, onSessionExpired, type Project, type ProjectSetup } from './lib/api.js';
import { queryClient } from './lib/queryClient.js';
import { CLIENT_SITE_URL } from './lib/config.js';
import { EnterPage, enterHref } from './pages/EnterPage.js';
import { InvitationPage } from './pages/InvitationPage.js';
import { JoinPage } from './pages/JoinPage.js';
import { ProjectsPage } from './pages/ProjectsPage.js';
import { NewSearchPage } from './pages/NewSearchPage.js';
import { NewProjectPage } from './pages/NewProjectPage.js';
import { StartProductionPage } from './pages/StartProductionPage.js';
import { ReviewScenesStep } from './pages/setup/ReviewScenesStep.js';
import { BreakdownStep } from './pages/setup/BreakdownStep.js';
import { ExploreStep } from './pages/setup/ExploreStep.js';
import { WorkflowChoiceStep } from './pages/setup/WorkflowChoiceStep.js';
import { ElementsStep } from './pages/setup/ElementsStep.js';
import { SetupFlow, workspacePath, type SetupStep } from './components/SetupFlow.js';
import { ConversationDrawer } from './components/conversations/ConversationDrawer.js';
import { ProjectPermissionsProvider } from './lib/permissions.js';
import { track } from './lib/telemetry.js';
import { TrackSetupStep } from './components/TrackView.js';
import { WorkspacePage } from './pages/WorkspacePage.js';

/**
 * `absent` — never had a session here. `expired` — had one, and the server has
 * since refused it. They are separated because the second one needs an
 * explanation: the producer was working a moment ago and is owed a reason.
 */
type SessionState = 'active' | 'absent' | 'expired';

/**
 * Every tab has an address.
 *
 * The workspace used to hold the open tab in component state, which meant a
 * producer could not send a colleague "the schedule for Northern Light", could
 * not bookmark the shortlist they review every morning, and lost their place on
 * every refresh. The tab slugs live in the shared contract
 * (`TAB_SLUGS`/`tabFromSlug`) rather than here, because those strings end up in
 * bookmarks and Slack messages the day this ships — a second copy in the router
 * would drift, and the drift presents as a dead link somebody saved months ago.
 */
export function App() {
  // A TOKEN IS NOT A SESSION. This used to be `getToken() !== null`, read once,
  // which is a question about `sessionStorage` and not about whether the server
  // still accepts it. When the JWT expired the answer stayed `true`, the app
  // rendered the workspace, every request 401'd, and the producer was told
  // "Project not found." about a production that exists — and a refresh said it
  // again, because the dead token was still in storage. The API helper now
  // reports the refusal and this state follows it.
  const [session, setSession] = useState<SessionState>(() =>
    getToken() === null ? 'absent' : 'active',
  );
  useEffect(
    () =>
      onSessionExpired(() => {
        setSession('expired');
      }),
    [],
  );

  // NO LOGIN FORM HERE, DELIBERATELY. The platform allows one active JWT per
  // user, so a second login in the workspace revoked the producer's session on
  // the main site and theirs revoked this one. Sign-in happens once, on
  // ScoutAround; this app only ever receives that session.
  if (session !== 'active') return <SendToClientSite expired={session === 'expired'} />;

  return (
    <>
      <TopBar
        onSignOut={() => {
          clearToken();
          // Signing out ends the ONE session, so it ends everywhere — which is
          // what a single sign-in implies and what the producer expects.
          window.location.assign(`${CLIENT_SITE_URL}/production-workspace`);
        }}
      />
      <RouteProgress />
      <Outlet />
    </>
  );
}

/**
 * WHILE A ROUTE DECIDES, THE PAGE STAYS. A loader that asks the server where
 * the production is takes a moment; the previous screen remains under a thin
 * bar rather than being replaced by "Loading…" — which is the flash the
 * effect-based guard used to cause on every step change.
 */
function RouteProgress() {
  const navigation = useNavigation();
  if (navigation.state !== 'loading') return null;

  return (
    <div
      role="progressbar"
      aria-label="Loading the next page"
      data-testid="route-loading"
      style={{ position: 'fixed', top: 0, left: 0, right: 0, height: 3, background: 'var(--color-accent)', zIndex: 20 }}
    />
  );
}

function InvitationRoute() {
  const { token } = useParams();

  if (!token) return <Navigate to="/" replace />;

  return <InvitationPage token={token} />;
}

/**
 * THE ROUTE TABLE, EXPORTED SO IT CAN BE ASSERTED.
 *
 * `/projects/new` and `/projects/new-search` are static segments sitting beside
 * `/projects/:projectId`. React Router ranks static above dynamic, so they win
 * — but the failure if that ever changed is silent and confusing: the address
 * would resolve to `/projects/new/overview`, the workspace would not find a
 * production called "new", and somebody would land back on the list wondering
 * why the button did nothing. `routes.test.tsx` mounts this and checks.
 *
 * THE SETUP PAGES AND THE `/setup` ENTRY CARRY LOADERS. Where a
 * production is on its guided path decides where these routes go, and that
 * decision is made in the loader — on an answer fetched for this navigation,
 * before anything renders — rather than in a component reading a cache that
 * the last screen filled. A screenplay landing on step 1 is what turns
 * `nextStep` from "start" into "scenes"; deciding on the cached value sent
 * "Review scenes →" straight back to step 1.
 */
export const workspaceRoutes: RouteObject[] = [
  { path: '/', element: <ProjectsRoute /> },
  // BEFORE the `:projectId` route on purpose. React Router ranks a static
  // segment above a dynamic one, so this would win anyway — but the failure if
  // it ever did not is a redirect to `/projects/new-search/overview`, which
  // reads as a broken link rather than a routing order.
  { path: '/projects/new-search', element: <NewSearchRoute /> },
  { path: '/projects/new', element: <NewProjectRoute step={1} /> },
  { path: '/projects/new/plan', element: <NewProjectRoute step={2} /> },
  // THE GUIDED FIRST PASS: static segments beside `:tabSlug`, none colliding
  // with a tab slug. `/setup` names no step — "Continue setup" — and resolves
  // to wherever the production is.
  { path: '/projects/:projectId/setup', loader: setupLoader(undefined), shouldRevalidate: setupShouldRevalidate, element: <SetupRoute />, errorElement: <SetupLoadError /> },
  { path: '/projects/:projectId/start', loader: setupLoader('start'), shouldRevalidate: setupShouldRevalidate, element: <SetupRoute step="start" />, errorElement: <SetupLoadError /> },
  { path: '/projects/:projectId/scenes', loader: setupLoader('scenes'), shouldRevalidate: setupShouldRevalidate, element: <SetupRoute step="scenes" />, errorElement: <SetupLoadError /> },
  // The choice between the two workflows, once the scenes are confirmed.
  { path: '/projects/:projectId/choose', loader: setupLoader('choose'), shouldRevalidate: setupShouldRevalidate, element: <SetupRoute step="choose" />, errorElement: <SetupLoadError /> },
  // The Locations workflow keeps the two addresses it had (bookmarks): its
  // requirements review, then Explore.
  { path: '/projects/:projectId/breakdown', loader: setupLoader('breakdown'), shouldRevalidate: setupShouldRevalidate, element: <SetupRoute step="breakdown" />, errorElement: <SetupLoadError /> },
  { path: '/projects/:projectId/explore', loader: setupLoader('explore'), shouldRevalidate: setupShouldRevalidate, element: <SetupRoute step="explore" />, errorElement: <SetupLoadError /> },
  // The selected requirement is part of the address: "the search for the hotel
  // room" is a link somebody can send.
  { path: '/projects/:projectId/explore/:requirementId', loader: setupLoader('explore'), shouldRevalidate: setupShouldRevalidate, element: <SetupRoute step="explore" />, errorElement: <SetupLoadError /> },
  // The detailed breakdown workflow: cast, extras, props, costumes, vehicles…
  { path: '/projects/:projectId/elements', loader: setupLoader('elements'), shouldRevalidate: setupShouldRevalidate, element: <SetupRoute step="elements" />, errorElement: <SetupLoadError /> },
  // A bare project URL is a real thing to paste; it lands on Overview rather
  // than 404ing. `replace` so Back does not bounce through it.
  { path: '/projects/:projectId', element: <Navigate to="overview" replace /> },
  // The optional trailing segments carry sub-views: a stripboard and a
  // comparison are places, and a place has an address.
  { path: '/projects/:projectId/:tabSlug', element: <WorkspaceRoute /> },
  { path: '/projects/:projectId/:tabSlug/:subSlug', element: <WorkspaceRoute /> },
  { path: '*', element: <Navigate to="/" replace /> },
];

/**
 * The whole application. Three routes work signed out: `/enter` is how a session
 * begins, and an invitation — by email or by a shared link (`/join`, 2026-11-02)
 * — is opened by someone who may have no account at all, the exact person the
 * feature exists for, so sending them to the sign-in wall before they can see
 * what they were invited to is the one thing this table must not do. `/join` is
 * also the one page an account without the Production workspace is handed to.
 * Everything else sits under the session gate.
 */
export const appRoutes: RouteObject[] = [
  { path: '/enter', element: <EnterPage /> },
  { path: '/invitations/:token', element: <InvitationRoute /> },
  { path: '/join', element: <JoinPage /> },
  { element: <App />, children: workspaceRoutes },
];

function NewProjectRoute({ step }: { step: 1 | 2 }) {
  const navigate = useNavigate();

  return (
    <NewProjectPage
      step={step}
      onStep={(next) => {
        void navigate(next === 2 ? '/projects/new/plan' : '/projects/new', { replace: next === 1 && step === 2 });
      }}
      /**
       * A NEW PRODUCTION OPENS ON THE SCRIPT, NOT ON THE OVERVIEW.
       *
       * Overview answers "where is this production, and what needs me?" — a
       * good question about a production that has been worked on, and a wall of
       * dashes on one created ten seconds ago. It counted scenes that do not
       * exist and requirements nobody has written, and offered nothing to do
       * about either.
       *
       * The screenplay is the first real act, so that is where creating one
       * ends. Overview is a tab away and keeps every word it says.
       */
      onCreated={(address) => {
        void navigate(`/projects/${address}/start`);
      }}
      onCancel={() => {
        void navigate('/');
      }}
    />
  );
}

/**
 * How far along the path a page sits. The choice and both workflows share a
 * rank: they open together, once the scenes are confirmed, and none of them is
 * a prerequisite of another.
 */
const STEP_RANK: Readonly<Record<SetupStep, number>> = {
  start: 0,
  scenes: 1,
  choose: 2,
  breakdown: 2,
  explore: 2,
  elements: 2,
};

/**
 * Where a setup URL goes, given where the production is. Pure, so the rule
 * can be asserted without a router:
 *
 * - the `/setup` entry goes to the next step;
 * - a page the production is not ready for goes to the one it is on — no
 *   workflow before the scenes are confirmed, no scenes before a script;
 * - FINISHED, only the entry and step 1 go to the workspace. The scenes, the
 *   choice and both workflows stay open, because either workflow must be
 *   reachable after the other (owner brief, 2026-10-29); before that, finishing
 *   sent every setup URL to the workspace and a producer who finished Locations
 *   could never come back to the detailed breakdown;
 * - otherwise the URL stands.
 */
export function setupRedirect(
  step: SetupStep | undefined,
  nextStep: ProjectSetup['nextStep'],
  address: string,
): string | null {
  if (nextStep === null) return step === undefined || step === 'start' ? workspacePath(address) : null;
  if (step === undefined) return `/projects/${address}/${nextStep}`;
  if (STEP_RANK[step] > STEP_RANK[nextStep]) return `/projects/${address}/${nextStep}`;

  return null;
}

/**
 * A SETUP PAGE'S LOADER ANSWERS FOR ITS PATH, not for its query string.
 *
 * Where a production is on its guided path is decided per page; `?filter=`,
 * `?q=` and `?scene=` are that page's own view state and change with every
 * click in a list. Re-running the loader for them asked the server for `/setup`
 * again on each scene picked and held the address change until it answered.
 */
export function setupShouldRevalidate({
  currentUrl,
  nextUrl,
  defaultShouldRevalidate,
}: ShouldRevalidateFunctionArgs): boolean {
  if (currentUrl.pathname === nextUrl.pathname) return false;

  return defaultShouldRevalidate;
}

interface SetupLoaderData {
  address: string;
  project: Project;
}

/** The loader's answer, checked rather than asserted: `useLoaderData` is untyped. */
function isSetupLoaderData(value: unknown): value is SetupLoaderData {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { address?: unknown }).address === 'string' &&
    typeof (value as { project?: unknown }).project === 'object'
  );
}

/**
 * THE LOADER FOR A SETUP STEP.
 *
 * Resolves the address the way `WorkspacePage` does — against the project
 * list, which is also the authorization — then asks the server where the
 * production is and honours the answer. The setup answer is fetched with
 * `staleTime: 0` so it is THIS navigation's answer and not the cache the last
 * screen left; the list can be a few seconds old, a production's address does
 * not change between two clicks.
 */
export function setupLoader(step: SetupStep | undefined) {
  return async ({ params }: LoaderFunctionArgs): Promise<SetupLoaderData | Response> => {
    const address = params.projectId;
    if (address === undefined) return redirect('/');

    const projects = await queryClient.fetchQuery({
      queryKey: ['projects'],
      queryFn: api.listProjects,
      staleTime: 5_000,
    });
    const target = parseProjectAddress(address);
    if (target === null) return redirect('/');
    const project = projects.find((p) =>
      'urlKey' in target ? p.urlKey === target.urlKey : p.id === target.projectId,
    );

    // Not this producer's, or gone: the workspace answers that, and one copy
    // of "not found" is enough.
    if (project === undefined) return redirect(workspacePath(address));

    // FINISHED IS FINAL for the way in. A production the cache already knows
    // is finished cannot become unfinished, so the `/setup` entry and step 1 —
    // browser Back, an old bookmark — go to the workspace without a round trip.
    // The other pages stay open after finishing (see `setupRedirect`).
    const known = queryClient.getQueryData<ProjectSetup>(['project-setup', project.id]);
    if (known?.setupCompletedAt && (step === undefined || step === 'start')) return redirect(workspacePath(address));

    const setup = await queryClient.fetchQuery({
      queryKey: ['project-setup', project.id],
      queryFn: () => api.projectSetup(project.id),
      staleTime: 0,
    });

    const to = setupRedirect(step, setup.nextStep, address);
    if (to !== null) return redirect(to);

    return { address, project };
  };
}

/** A loader that could not read the production: say so, offer the way out. */
function SetupLoadError() {
  const { projectId: address } = useParams();

  return (
    <div style={{ padding: 40 }} data-testid="setup-load-error">
      <p className="dh" style={{ fontSize: 20 }}>
        The production could not be loaded
      </p>
      <p className="mm" style={{ fontSize: 13, margin: '10px 0 0', lineHeight: 1.6 }}>
        Nothing is lost. Reload in a moment, or open the project directly.
      </p>
      {address === undefined ? null : (
        <a className="btn btn-ghost" style={{ marginTop: 16, display: 'inline-flex' }} href={workspacePath(address)}>
          Open the project
        </a>
      )}
    </div>
  );
}

/**
 * ONE ROUTE FOR THE FOUR STEPS.
 *
 * By the time this renders, the loader has resolved the production and decided
 * that this step is the right place to be. The permissions provider is mounted
 * here for the same reason it is mounted inside the workspace's resolved
 * branch: what somebody may do is a fact about (person, project), and the
 * Explore step's Shortlist and Reject read it.
 */
function SetupRoute({ step }: { step?: SetupStep }) {
  const loaded: unknown = useLoaderData();
  if (!isSetupLoaderData(loaded)) throw new Error('The setup route rendered without its loader data');
  const { address, project } = loaded;
  const navigate = useNavigate();
  const location = useLocation();
  const { requirementId } = useParams();
  const [, setSearchParams] = useSearchParams();

  // The `/setup` entry always redirects in its loader; a render here without
  // a step is a URL the table does not produce.
  if (step === undefined) return <Navigate to={workspacePath(address)} replace />;

  const go = (to: SetupStep) => {
    void navigate(`/projects/${address}/${to}`);
  };
  // Leaving the flow REPLACES the step's history entry, so Back from the
  // workspace does not land on a setup URL the loader then bounces.
  const toWorkspace = (tab: 'OVERVIEW' | 'LOCATIONS' | 'SCRIPT_AND_SCENES' = 'OVERVIEW') => {
    void navigate(workspacePath(address, tab), { replace: true });
  };
  // Skip writes nothing: reopening the production lands on this same step.
  // It is only RECORDED, for setup drop-off (BI audit gap 25).
  const skip = () => {
    track(project.id, 'setup_step_skipped', { step });
    toWorkspace();
  };
  return (
    <ProjectPermissionsProvider projectId={project.id}>
      {/* The setup screens carry "Discuss" controls too; the drawer they open lives here. */}
      <ConversationDrawer projectId={project.id} />
      <SetupFlow
        project={project}
        step={step}
        onBackToProject={() => {
          toWorkspace();
        }}
        // Steps 1 and 2 and the choice keep their way out in their own action bar.
        stepOwnsExit={step === 'start' || step === 'scenes' || step === 'choose'}
        narrow={step === 'start' || step === 'choose'}
        onNavigate={go}
      >
        <TrackSetupStep projectId={project.id} step={step} />
        {step === 'start' ? (
          <StartProductionPage
            projectId={project.id}
            onContinue={() => {
              go('scenes');
            }}
            onSkip={skip}
            // No screenplay: the workspace's Locations tab, where New Location
            // Search sends the same producer. Writes nothing, like Skip.
            onFindLocations={() => {
              toWorkspace('LOCATIONS');
            }}
          />
        ) : null}
        {step === 'scenes' ? (
          <ReviewScenesStep
            projectId={project.id}
            // Under the router the scene list owns its own address
            // (`?filter=&q=&scene=`, components/scenes/sceneAddress.tsx).
            onContinue={() => {
              go('choose');
            }}
            // Every correction is already saved as it is made, so leaving is
            // only leaving; recorded like Skip, for setup drop-off.
            onFinishLater={skip}
          />
        ) : null}
        {step === 'choose' ? (
          <WorkflowChoiceStep
            projectId={project.id}
            onLocations={() => {
              go('breakdown');
            }}
            onBreakdown={() => {
              go('elements');
            }}
            onReviewScenes={(filter) => {
              void navigate(`/projects/${address}/scenes${filter === 'ALL' ? '' : `?filter=${filter}`}`);
            }}
            onWorkspace={() => {
              toWorkspace();
            }}
          />
        ) : null}
        {step === 'breakdown' ? (
          <BreakdownStep
            projectId={project.id}
            projectTitle={project.title}
            focusSceneId={new URLSearchParams(location.search).get('scene')}
            // The scene on screen is part of the address, so a link sends someone to it.
            onSceneChange={(sceneId) => {
              setSearchParams(
                (current) => {
                  const next = new URLSearchParams(current);
                  next.set('scene', sceneId);
                  return next;
                },
                { replace: true },
              );
            }}
            onContinueToMatching={() => {
              go('explore');
            }}
          />
        ) : null}
        {step === 'explore' ? (
          <ExploreStep
            projectId={project.id}
            projectTitle={project.title}
            selectedId={requirementId ?? null}
            onSelect={(id) => {
              void navigate(`/projects/${address}/explore/${id}`, { replace: true });
            }}
            onBackToBreakdown={() => {
              go('breakdown');
            }}
            onCompare={(requirementId) => {
              void navigate(`/projects/${address}/${TAB_SLUGS.LOCATIONS}/${requirementId}`);
            }}
            // Finishing Locations returns to the choice, where the detailed
            // breakdown waits with its own status — never to a workspace that
            // hides it.
            onFinish={() => {
              go('choose');
            }}
          />
        ) : null}
        {step === 'elements' ? (
          <ElementsStep
            projectId={project.id}
            projectTitle={project.title}
            onDone={() => {
              go('choose');
            }}
          />
        ) : null}
      </SetupFlow>
    </ProjectPermissionsProvider>
  );
}

function NewSearchRoute() {
  const navigate = useNavigate();

  return (
    <NewSearchPage
      onOpen={(projectId, tabSlug) => {
        void navigate(`/projects/${projectId}/${tabSlug}`);
      }}
      onBack={() => {
        void navigate('/');
      }}
    />
  );
}

function ProjectsRoute() {
  const navigate = useNavigate();

  return (
    <ProjectsPage
      onOpen={(projectId) => {
        void navigate(`/projects/${projectId}/overview`);
      }}
      onContinueSetup={(address) => {
        void navigate(`/projects/${address}/setup`);
      }}
      onNewProject={() => {
        void navigate('/projects/new');
      }}
      onNewSearch={() => {
        void navigate('/projects/new-search');
      }}
    />
  );
}

function WorkspaceRoute() {
  // `projectId` is the route PARAM NAME and no longer the shape of the value:
  // the segment is an address (`northern-light-7fq2k9xd`), which WorkspacePage
  // resolves against the project list it already loads.
  const { projectId: address, tabSlug, subSlug } = useParams();
  const navigate = useNavigate();

  if (!address) return <Navigate to="/" replace />;

  return (
    <WorkspacePage
      address={address}
      tabSlug={tabSlug}
      subSlug={subSlug}
      onNavigate={(path) => {
        void navigate(path);
      }}
      onBack={() => {
        void navigate('/');
      }}
    />
  );
}

function TopBar({ onSignOut }: { onSignOut: () => void }) {
  return (
    <header
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        // The gutter matches the pages below it, and shrinks with them: at 40px
        // a side, a 360px screen spent a fifth of itself on margins and both
        // the kicker and the sign-out broke across two lines.
        padding: '12px clamp(16px, 4vw, 40px)',
        borderBottom: '2px solid var(--color-divider)',
        background: 'var(--color-bg)',
      }}
    >
      <span className="dh" style={{ fontSize: 18 }}>
        ScoutAround
      </span>
      <span className="kick npg-kicker">Production workspace</span>
      <button
        type="button"
        className="ctl"
        style={{ marginLeft: 'auto', whiteSpace: 'nowrap' }}
        data-testid="sign-out"
        onClick={onSignOut}
      >
        Sign out
      </button>
    </header>
  );
}

/**
 * The workspace has no sign-in of its own, so an unauthenticated visitor is
 * sent to the one that exists — carrying where they were going, so a shared
 * deep link survives the round trip. That carry is what makes this the right
 * screen for an EXPIRED session too: signing in again returns the producer to
 * the exact tab they were on rather than to the project list.
 */
function SendToClientSite({ expired = false }: { expired?: boolean }) {
  const location = useLocation();
  // Through `/enter`, so this tab mints the sign-in `state` before it leaves.
  const target = enterHref(location.pathname + location.search);

  return (
    <main
      data-testid="needs-signin"
      style={{
        minHeight: '60vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <div className="card1" style={{ width: 460, maxWidth: '100%', padding: 32 }}>
        <p className="kick">Production workspace</p>
        <h1
          className="dh"
          style={{ fontSize: 30, marginTop: 10 }}
          data-testid={expired ? 'session-expired' : 'session-absent'}
        >
          {expired ? 'Your session has ended' : 'Sign in on ScoutAround'}
        </h1>
        <p className="mm" style={{ fontSize: 13, marginTop: 12, lineHeight: 1.6 }}>
          {expired
            ? // Said plainly, and said to be reassuring: an expiry looks
              // identical to data loss from where the producer is sitting.
              'Nothing was lost. Sessions end after a while, or when you sign in on another device — sign in again and you will come straight back to this page.'
            : 'One account, one sign-in. Open the workspace from your profile menu on ScoutAround and you will land straight back here.'}
        </p>
        <a
          className="btn btn-primary"
          href={target}
          data-testid="go-to-client-signin"
          style={{ marginTop: 18 }}
        >
          {expired ? 'Sign in again' : 'Continue to ScoutAround'}
        </a>
      </div>
    </main>
  );
}
